import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../base/hooks';

import { VacancyTableFilter } from '../../enums';
import { vacancyListActions } from '../../redux/slices/vacancyListSlice';
import { Pagination } from '../../../../../base/components/Pagination';
import { UpgradeModal } from '../UpgradeModal';
import { VacancyMobileListItem } from './VacancyMobileListItem';
import { NavBar } from '../VacancyTable/NavBar';
import { SearchBar } from '../VacancyTable/SearchBar';

import './styles.scss';
import { Button } from '../../../../../base/components/Button';
import { __t } from '../../../../../base/utils/t';
import { Icon } from '../../../../../base/components/Icon';
import { SortDirection } from '../../../../../base/enums';

export interface Props {
    filter: VacancyTableFilter;
}

const defaultSort = {
    column: 'updatedAt',
    direction: SortDirection.Desc,
};

export const VacancyMobileList = ({ filter }: Props) => {
    const dispatch = useAppDispatch();
    const { vacancies, currentPage, totalCount, shouldReloadLastFilter } = useAppSelector((state) => state.vacancyList);
    const { currency } = useAppSelector((state) => state.employerApp.config);
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState(defaultSort);
    const [upgradeVacancyId, setUpgradeVacancyId] = useState('');
    const [showUpgradeVacancyModal, setShowUpgradeVacancyModal] = useState(false);

    useEffect(() => {
        if (shouldReloadLastFilter) {
            dispatch(
                vacancyListActions.fetchVacancyList({
                    status: filter,
                    search,
                    sort,
                    page: currentPage,
                }),
            );
            dispatch(vacancyListActions.setShouldReloadLastFilter(false));
        }
    }, [shouldReloadLastFilter, filter, search, sort, currentPage]);

    useEffect(() => {
        setSearch('');
        setSort(defaultSort);
        dispatch(vacancyListActions.fetchVacancyList({ status: filter, sort: defaultSort, page: 1 }));
    }, [filter]);

    const searchChangeHandler = (value: string) => setSearch(value);

    const clearSearch = () => {
        setSearch('');
        setSort(defaultSort);
        dispatch(
            vacancyListActions.fetchVacancyList({
                status: filter,
                sort: defaultSort,
                page: 1,
            }),
        );
    };

    const submitSearch = () => {
        setSort(defaultSort);
        dispatch(
            vacancyListActions.fetchVacancyList({
                status: filter,
                search,
                sort: defaultSort,
                page: 1,
            }),
        );
    };

    const paginationHandler = (page: number) => {
        dispatch(
            vacancyListActions.fetchVacancyList({
                status: filter,
                search,
                sort,
                page,
            }),
        );
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    };

    const upgradeVacancyHandler = (vacancyId: string) => {
        setUpgradeVacancyId(vacancyId);
        setShowUpgradeVacancyModal(true);
    };

    const hideUpgeradeVacancyModal = () => {
        setUpgradeVacancyId('');
        setShowUpgradeVacancyModal(false);
    };

    const listData = useMemo(() => {
        if (!vacancies.length && search) {
            return (
                <div className='vacancy-table-component__search-nothing-found'>
                    <Icon
                        icon='magnifying-glass-thin'
                        size='64'
                        color='info'
                        className='vacancy-table-component__search-nothing-found-icon'
                    />
                    <p className='vacancy-table-component__search-nothing-found-title'>{__t('Ничего не найдено')}</p>
                    <p className='vacancy-table-component__search-nothing-found-text'>
                        {__t('Попробуйте снова поиск отредактировав название')}
                    </p>
                    <div className='vacancy-table-component__search-nothing-found-clear-link' onClick={clearSearch}>
                        {__t('Очистить параметры фильтра')}
                    </div>
                </div>
            );
        }

        if (!vacancies.length) {
            return <div className='vacancy-mobile-list__nothing-found-message'>{__t('Нет вакансий')}</div>;
        }

        return vacancies.map((vacancy) => (
            <VacancyMobileListItem
                key={vacancy.id}
                vacancyData={vacancy}
                currencies={currency}
                upgradeVacancyHandler={upgradeVacancyHandler}
            />
        ));
    }, [vacancies]);

    return (
        <div className='vacancy-mobile-list'>
            <div className='razem-app__wrapper'>
                <NavBar />

                <div className='vacancy-mobile-list__searchbar'>
                    <SearchBar
                        value={search}
                        changeHandler={searchChangeHandler}
                        submitHandler={submitSearch}
                        clearSearch={clearSearch}
                    />
                </div>

                <div className='vacancy-mobile-list__content'>{listData}</div>

                <Pagination currentPage={currentPage} totalCount={totalCount} paginationHandler={paginationHandler} />
            </div>

            <Button className='vacancy-mobile-list__add-vacancy-button' routerLinkPath='/vacancy/create'>
                {__t('Добавить вакансию')}
            </Button>

            <UpgradeModal
                vacancyId={upgradeVacancyId}
                visible={showUpgradeVacancyModal}
                closeModalHandler={hideUpgeradeVacancyModal}
            />
        </div>
    );
};
